import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  FormControl,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import color from "../../../components/utils/Colors";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useNavigate } from "react-router-dom";
import { courseCategory, time } from "../../../components/utils/Data";
import { createCourse } from "../../../services/services";
import { useUser } from "../../../services/Context/UserContext";
import { useSnackbar } from "../../../components/utils/SnackBar";

interface CreateCourseStepsProps {
  open: boolean;
  handleClose: () => void;
}

const CreateCourseSteps: React.FC<CreateCourseStepsProps> = ({
  open,
  handleClose,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const showSnackbar = useSnackbar();
  const { user } = useUser();
  const [formData, setFormData] = useState({
    title: "",
    categoryId: "" as string | number,
    selectedText: "", // timeline
  });

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleNext = () => {
    if (step < 2) setStep(step + 1);
  };

  const handlePrevious = () => {
    if (step === 0) {
      handleClose();
    } else {
      setStep(step - 1);
    }
  };

  const handleTextSelect = (text: string) => {
    setFormData({ ...formData, selectedText: text });
  };

  const isDisabled =
    (step === 0 &&
      (formData.title.trim() === "" || formData.title.length < 5)) ||
    (step === 1 &&
      (formData.categoryId === "" || formData.categoryId === null)) ||
    (step === 2 && formData.selectedText === "");

  // Step 2 (index 2) is the final step — call the API here
  const handleFinish = async () => {
    if (step < 2) {
      handleNext();
      return;
    }

    // Final step: call createCourse API
    setLoading(true);
    try {
      const response = await createCourse({
        title: formData.title,
        categoryId: formData.categoryId,
        timeline: formData.selectedText,
        createdBy: user?.id,
        status: "draft",
        teachersName: user?.name,
      });

      // Extract courseId from the API response
      const courseId =
        response.data?.data?.id || response.data?.id || response.data?.courseId;

      if (!courseId) {
        throw new Error("Course ID not returned from server.");
      }

      handleClose();
      // Pass courseId to the next step so every downstream page has it
      navigate("/course-desc", { state: { courseId } });
    } catch (err: any) {
      showSnackbar(
        err.response?.data?.message ||
          err.message ||
          "Failed to create course. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const renderModalContent = () => {
    switch (step) {
      case 0:
        return (
          <div
            style={{
              border: "solid 2px",
              borderColor: color.firstColor,
              borderRadius: "60px 2px 60px 2px",
              padding: "40px 30px",
              paddingBottom: "60px",
              marginTop: "20px",
            }}
          >
            <Typography
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                color: color.textColor,
                fontStyle: "italic",
                fontSize: "20px",
              }}
            >
              How would your course title be?
            </Typography>
            <Typography
              sx={{
                textAlign: "center",
                color: color.textColor,
                fontSize: "12px",
                marginBottom: "10px",
                marginTop: "10px",
              }}
            >
              (You can change the title later)
            </Typography>
            <TextField
              className="input-root"
              placeholder="e.g. Learn Python from Beginning"
              name="title"
              fullWidth
              margin="normal"
              value={formData.title}
              onChange={handleInputChange}
              inputProps={{ maxLength: 50 }}
              error={formData.title.length > 50}
              helperText={
                formData.title.length > 50
                  ? "Title cannot exceed 50 characters"
                  : formData.title.length > 0 && formData.title.length < 5
                    ? "Title must be at least 5 characters long"
                    : ""
              }
            />
          </div>
        );

      case 1:
        return (
          <div
            style={{
              border: "solid 2px",
              borderColor: color.firstColor,
              borderRadius: "60px 2px 60px 2px",
              padding: "40px 30px",
              paddingBottom: "60px",
              marginTop: "20px",
            }}
          >
            <Typography
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                color: color.textColor,
                fontStyle: "italic",
                fontSize: "20px",
              }}
            >
              Choose a category that best fits your course
            </Typography>
            <Typography
              sx={{
                textAlign: "center",
                color: color.textColor,
                fontSize: "12px",
                marginBottom: "10px",
                marginTop: "10px",
              }}
            >
              (You can change the category later)
            </Typography>

            <FormControl margin="normal" fullWidth className="input-root">
              <Select
                // Single-select — API expects one categoryId
                value={formData.categoryId}
                onChange={(e) =>
                  setFormData({ ...formData, categoryId: e.target.value })
                }
                displayEmpty
                renderValue={(selected) => {
                  if (selected === "" || selected === null) {
                    return (
                      <Typography style={{ color: "rgba(0,0,0,0.54)" }}>
                        Choose category
                      </Typography>
                    );
                  }
                  // Find label for the selected value
                  const found = (courseCategory as any[]).find(
                    (c) => (typeof c === "object" ? c.value : c) === selected,
                  );
                  return typeof found === "object"
                    ? found.label
                    : String(selected);
                }}
              >
                {(courseCategory as any[]).map((option) => {
                  const val =
                    typeof option === "object" ? option.value : option;
                  const label =
                    typeof option === "object" ? option.label : option;
                  return (
                    <MenuItem key={val} value={val}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
        );

      case 2:
        return (
          <div
            style={{
              border: "solid 2px",
              borderColor: color.firstColor,
              borderRadius: "60px 2px 60px 2px",
              padding: "40px 30px",
              paddingBottom: "60px",
              marginTop: "20px",
            }}
          >
            <Typography
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                color: color.textColor,
                fontStyle: "italic",
                fontSize: "20px",
                marginBottom: "25px",
              }}
            >
              How much time can you spend on creating your course?
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: "30px",
                flexWrap: "wrap",
                justifyContent: "space-evenly",
              }}
            >
              {(time as string[]).map((text) => (
                <Button
                  key={text}
                  sx={{
                    display: "block",
                    mb: 1,
                    boxShadow: 3,
                    textTransform: "none",
                    borderRadius: "2px 20px 2px 20px",
                    padding: "5px 15px",
                    color: color.firstColor,
                    border:
                      formData.selectedText === text
                        ? `2px solid ${color.firstColor}`
                        : "2px solid transparent",
                  }}
                  onClick={() => handleTextSelect(text)}
                >
                  {text}
                </Button>
              ))}
            </Box>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "100vw", md: "65vw" },
          margin: 0,
          maxWidth: "100vw",
          height: { xs: "auto", md: "85vh" },
          paddingBottom: { xs: "40px", md: "0px" },
        },
      }}
    >
      <Box
        sx={{
          backgroundImage: "url(/assets/modal_bg.png)",
          backgroundRepeat: "repeat-y",
          backgroundSize: "cover",
          height: "100%",
        }}
      >
        <DialogTitle style={{ display: "flex", alignItems: "center" }}>
          <Typography
            onClick={handlePrevious}
            sx={{
              background: "none",
              color: color.textColor,
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: "30px",
              cursor: "pointer",
            }}
          >
            <ChevronLeftIcon /> Back
          </Typography>

          <Typography
            sx={{
              color: color.firstColor,
              border: "solid 1px",
              borderColor: color.firstColor,
              width: "fit-content",
              borderRadius: "30px",
              padding: "3px 15px",
              fontSize: "14px",
            }}
          >
            {`Step ${step + 1} of 3`}
          </Typography>
        </DialogTitle>

        {/* Progress bar */}
        <div style={{ background: "#cba4fd", marginBottom: "10px" }}>
          <div
            style={{
              height: "4px",
              background: color.firstColor,
              borderRadius: "0px 10px 10px 0px",
              width: step === 0 ? "33%" : step === 1 ? "66%" : "100%",
              transition: "width 0.3s ease",
            }}
          />
        </div>

        <DialogContent>{renderModalContent()}</DialogContent>

        <DialogActions>
          <Button
            id={isDisabled || loading ? "disabled-button" : "button"}
            onClick={handleFinish}
            disabled={isDisabled || loading}
            sx={{ ml: "auto", bottom: 0, right: 20 }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                Creating...
              </Box>
            ) : step === 2 ? (
              "Finish"
            ) : (
              "Next"
            )}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CreateCourseSteps;
